import Spinbox from 'components/Forms/Spinbox/Spinbox';
import { HighButtonStyled, LoyaltyClubSpinboxWrapperStyled } from 'components/Pages/Cart/LoyaltyClub/LoyaltyClub.style';
import { useApplyLoyaltyPointsToCart } from 'hooks/cart/useApplyLoyaltyPointsToCart';
import useTranslation from 'next-translate/useTranslation';
import { SpinnerIcon } from 'public/svg/IconsSvg';
import { useCallback, useEffect, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { useTheme } from 'styled-components';
import twMerge from 'utils/twMerge';

type LoyaltyClubUsePointsFormProps = {
    onPointsToBeAppliedValueChange?: (value: number) => void;
    maximumApplicableLoyaltyPoints: number;
    applyLoyaltyPointsButtonDisabled?: boolean;
    pointsApplied?: number;
    className?: string;
};

export const LoyaltyClubUsePointsForm: FC<LoyaltyClubUsePointsFormProps> = ({
    onPointsToBeAppliedValueChange,
    maximumApplicableLoyaltyPoints,
    pointsApplied = 0,
    applyLoyaltyPointsButtonDisabled = false,
    className,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [pointsToBeApplied, setPointsToBeApplied] = useState(pointsApplied);
    const [applyLoyaltyPointsToCart, isApplyLoyaltyPointsToCartFetching] = useApplyLoyaltyPointsToCart();
    const updateUserState = usePersistStore((s) => s.updateUserState);

    useEffect(() => {
        setPointsToBeApplied(pointsApplied);
    }, [pointsApplied]);

    const onSpinboxPointsValueChange = (value: number) => {
        setPointsToBeApplied(Math.max(value, 0));
        onPointsToBeAppliedValueChange?.(value);
    };

    const onApplyLoyaltyClubPointsHandler = useCallback(async () => {
        if (pointsToBeApplied > 0) {
            await applyLoyaltyPointsToCart(pointsToBeApplied, {
                success: t('You have successfully applied {{ count }} TESCOMA club points to your cart.', {
                    count: pointsToBeApplied,
                }),
                error: t('Could not apply TESCOMA club points to your cart.'),
            });
            updateUserState({ loyaltyClubPoints: pointsToBeApplied });
        }
    }, [applyLoyaltyPointsToCart, t, updateUserState, pointsToBeApplied]);

    return (
        <div
            className={twMerge(
                'relative mt-6 mb-3 flex flex-col justify-start gap-5 sm:flex-row sm:gap-6 lg:w-full vl:justify-end vl:gap-6',
                className,
            )}
        >
            <LoyaltyClubSpinboxWrapperStyled>
                <Spinbox
                    id="loyalty-club-points-spinbox"
                    idSuffix="-loyalty-club"
                    defaultValue={pointsToBeApplied}
                    min={0}
                    step={1}
                    max={Math.max(maximumApplicableLoyaltyPoints, 0)}
                    text={t('Points', {
                        count: pointsToBeApplied,
                    })}
                    backgroundColor={theme.color.white}
                    border={`1px solid ${theme.color.grayDark}`}
                    hasDynamicWidth
                    overstepMessage={{
                        tooHigh: t('More points cannot be added'),
                    }}
                    onChangeValueCallback={onSpinboxPointsValueChange}
                />
            </LoyaltyClubSpinboxWrapperStyled>
            <HighButtonStyled
                type="button"
                onClick={onApplyLoyaltyClubPointsHandler}
                size="small"
                isDisabled={applyLoyaltyPointsButtonDisabled || isApplyLoyaltyPointsToCartFetching}
            >
                {isApplyLoyaltyPointsToCartFetching && <SpinnerIcon className="h-4 w-4" />}
                {t('Use my points')}
            </HighButtonStyled>
        </div>
    );
};
